import { batch } from 'react-redux';

import {
  SET_ALL_GRAPHICS,
  SET_GRAPHICS_SECTIONS,
  SET_SELFIE_MEDIA,
  SET_GRAPHICS_SECTION,
  SET_GRAPHICS_SELECTED_IND,
  SET_MULTISELECT_VALUE,
  SET_WATERMARKS,
  DELETE_GRAPHICS_ITEM,
  ADD_GRAPHICS_ITEM,
  SET_GRAPHICS_ERROR,
  UPLOAD_GRAPHICS_FILE,
  UPLOAD_GRAPHICS_FILE_DONE,
  SET_CUSTOM_ICON_FILE,
  SET_CUSTOM_ICON_ERROR,
  UPLOAD_CUSTOM_ICON_START,
  UPLOAD_CUSTOM_ICON_DONE,
} from './types';

import { updateEventImages } from './api';
import { uploadImage as uploadFileApi, updateEventsGeneric } from '../../../helpers/api';
import { displaySuccessModal, displayErrorModal } from '../../../helpers/swal';
import Alert from '../../../components/Alert';

export const setImages = (payload) => ({
  type: SET_ALL_GRAPHICS,
  payload,
});

export const setGraphicsSections = (sections) => ({
  type: SET_GRAPHICS_SECTIONS,
  payload: sections,
});

/**
 * Populate selfie sections from selfieCam.media (including media.watermarks).
 * Payload: { media: object, watermarks: array }.
 */
export const loadSelfieMedia = ({ media = {}, watermarks = [] } = {}) => ({
  type: SET_SELFIE_MEDIA,
  payload: { media, watermarks },
});

export const setWatermarks = (watermarks) => ({
  type: SET_WATERMARKS,
  payload: { watermarks },
});

/**
 * Save selfie cam graphics by writing selfieCam.media via the generic update endpoint.
 * Watermarks are stored under selfiecam.media.watermarks.
 */
export const selfieSave = (eventIds) => async (dispatch, getState) => {
  const { graphics: { sections } } = getState();

  const media = {};
  let watermarks = [];

  sections.forEach((section) => {
    if (section.key === 'watermarks') {
      watermarks = section.data?.watermarks || [];
      return;
    }
    if (!section.mediaKey) return;
    const url = section.data?.[`${section.key}Url`] || null;
    if (url) media[section.mediaKey] = url;
  });

  const watermarksPayload = watermarks.map((wm) => ({
    watermarkKey: wm.watermarkKey,
    watermarkRawUrl: wm.watermarkRawUrl || wm.watermarkKey,
    corner: wm.corner || 'top-left',
    wmWidthPct: typeof wm.wmWidthPct === 'number' ? wm.wmWidthPct : 16,
    padXPct: typeof wm.padXPct === 'number' ? wm.padXPct : 2,
    padYPct: typeof wm.padYPct === 'number' ? wm.padYPct : 2,
    wmScaleMode: wm.wmScaleMode || 'contain',
  }));

  const mediaWithWatermarks = { ...media, watermarks: watermarksPayload };

  if (!eventIds?.length) throw new Error('No events selected to update.');

  try {
    await updateEventsGeneric(eventIds, {
      'selfiecam.media': mediaWithWatermarks,
    });
    displaySuccessModal({ text: 'Event successfully updated' });
  } catch (err) {
    displayErrorModal({ text: err?.message || 'Failed to save graphics.' });
    throw err;
  }
};

export const uploadWatermark = (index, file) => async (dispatch, getState) => {
  const {
    events: { selectedEvents: [{ slugDate }] },
    graphics: { sections },
  } = getState();

  const watermarksSection = sections.find((s) => s.key === 'watermarks');
  const currentWatermarks = watermarksSection?.data?.watermarks || [];

  try {
    const data = await uploadFileApi(`watermark${index}`, slugDate, file);
    const url = data?.[`watermark${index}Url`] || data?.[`watermark${index}RawUrl`];
    const rawUrl = data?.[`watermark${index}RawUrl`] || url;
    if (!url) throw new Error('Upload succeeded but no URL returned');

    const newEntry = {
      watermarkKey: url,
      watermarkRawUrl: rawUrl,
      corner: 'top-left',
      wmWidthPct: 16,
      padXPct: 2,
      padYPct: 2,
    };

    const updated = [...currentWatermarks];
    if (index < updated.length) {
      updated[index] = { ...updated[index], ...newEntry };
    } else {
      updated.push(newEntry);
    }
    dispatch(setWatermarks(updated));
  } catch (err) {
    console.error(err);
    displayErrorModal({ text: err?.body?.error || err?.message || 'Watermark upload failed' });
    throw err;
  }
};

export const setSection = (payload) => ({
  type: SET_GRAPHICS_SECTION,
  payload,
});

export const setSelected = (selectedInd) => ({
  type: SET_GRAPHICS_SELECTED_IND,
  payload: { selectedInd },
});

export const setMultiselectValue = (value) => ({
  type: SET_MULTISELECT_VALUE,
  payload: { value },
});

export const addFile = ({ file, data }) => ({
  type: ADD_GRAPHICS_ITEM,
  payload: { file, data },
});

export const setError = (error) => ({
  type: SET_GRAPHICS_ERROR,
  payload: { error },
});

const deleteImg = () => ({ type: DELETE_GRAPHICS_ITEM });

export const deleteImage = () => async (dispatch) => {
  const { value: confirmed } = await Alert.fire({
    title: 'Do you want to delete the image?',
    type: 'question',
    html: 'Note that the changes are saved only on "Update event"',
    showCancelButton: true,
    showLoaderOnConfirm: true,
    allowOutsideClick: false,
  });

  if (confirmed) {
    dispatch(deleteImg());
  }
};

export const save = (eventIds) => async (dispatch, getState) => {
  const { graphics: { sections } } = getState();
  const images = {};

  sections.forEach(({
    data,
    value,
    key,
    customIconData,
  }) => {
    if (data) {
      Object.assign(images, data);
    } else {
      Object.assign(images, { [key]: value });
    }
    if (key === 'venueType') {
      if (value === 'custom') {
        if (customIconData?.srsIconUrl) {
          Object.assign(images, customIconData);
        } else {
          Object.assign(images, {
            srsIcon: null,
            srsIconUrl: null,
            srsIconRawUrl: null,
          });
        }
      }
    }
  });

  try {
    await updateEventImages({ eventIds, images });
    displaySuccessModal({ text: 'Event successfully updated' });
  } catch (err) {
    console.error(err);
    displayErrorModal({
      title: 'Failed to update overlays',
      text: (err.body?.error?.message) || err.message,
    });
  }
};

const uploadFileStart = () => ({
  type: UPLOAD_GRAPHICS_FILE,
});
const uploadFileDone = () => ({
  type: UPLOAD_GRAPHICS_FILE_DONE,
});

export const uploadFile = (file) => async (dispatch, getState) => {
  const {
    events: { selectedEvents: [{ slugDate }] },
    graphics: { sections, selectedInd },
  } = getState();
  const { key } = sections[selectedInd];

  dispatch(uploadFileStart());

  try {
    const data = await uploadFileApi(key, slugDate, file);
    batch(() => {
      dispatch(uploadFileDone());
      dispatch(addFile({ data }));
    });
  } catch (err) {
    console.error(err);
    batch(() => {
      dispatch(setError(err.body.error));
      dispatch(deleteImg);
    });
  }
};

export const setCustomIconFile = ({ file, data }) => ({
  type: SET_CUSTOM_ICON_FILE,
  payload: { file, data },
});

export const setCustomIconError = (error) => ({
  type: SET_CUSTOM_ICON_ERROR,
  payload: { error },
});

const uploadCustomIconStart = () => ({
  type: UPLOAD_CUSTOM_ICON_START,
});

const uploadCustomIconDone = () => ({
  type: UPLOAD_CUSTOM_ICON_DONE,
});

export const uploadCustomIcon = (file) => async (dispatch, getState) => {
  const {
    events: { selectedEvents: [{ slugDate }] },
  } = getState();

  dispatch(uploadCustomIconStart());

  try {
    const data = await uploadFileApi('srsIcon', slugDate, file);
    batch(() => {
      dispatch(uploadCustomIconDone());
      dispatch(setCustomIconFile({ data }));
    });
  } catch (err) {
    console.error(err);
    batch(() => {
      dispatch(setCustomIconError(err.body?.error || 'Failed to upload custom icon'));
    });
  }
};

export const deleteCustomIcon = () => async (dispatch) => {
  const { value: confirmed } = await Alert.fire({
    title: 'Do you want to delete the custom icon?',
    type: 'question',
    html: 'Note that the changes are saved only on "Update event"',
    showCancelButton: true,
    showLoaderOnConfirm: true,
    allowOutsideClick: false,
  });

  if (confirmed) {
    dispatch(setCustomIconFile({ file: null, data: {} }));
  }
};
